// Tipos para Chamados da API Prospera Sistemas

export interface ChamadoTipo {
  id: string;
  descricao: string;
}

export interface ChamadoTipoAtendimento {
  id: string;
  descricao: string;
}

export interface ChamadoNatureza {
  id: string;
  descricao: string;
}

export interface ChamadoStatus {
  id: string;
  descricao: string;
}

export interface ChamadoPrioridade {
  id: string;
  descricao: string;
  cor: string;
}

export interface ChamadoData {
  real: string;
  formatada: string;
  diff?: string;
  diferenca?: number;
  string?: string;
}

export interface ChamadoEmpresa {
  id: number;
  cnpj: string;
  nome: string;
}

export interface ChamadoResponsavel {
  id: number;
  nome: string;
}

export interface ChamadoProjetoCategoria {
  id: number;
  nome: string;
}

export interface ChamadoProjeto {
  id: number;
  nome: string;
  fechado: string | null;
  categoria: ChamadoProjetoCategoria;
}

export interface ChamadoProduto {
  id: number;
  nome: string;
  icone: string;
}

export interface ChamadoDataConclusaoPrevista {
  real: string;
  formatada: string;
}

export interface ChamadoDepartamento {
  id: number;
  nome: string;
}

export interface ChamadoSLA {
  executado: string;
  prevista: string;
  previstas: number;
  executados: number;
  porcentagem: number;
}

export interface ChamadoUltimaMovimentacao {
  data: string;
  diferenca: number;
  string: string;
}

export interface ChamadoVinculado {
  id: number | null;
  solicitacao: string | null;
  /** Presente na API em alguns retornos; usado quando o chamado vinculado não está na lista local. */
  descricao?: string | null;
}

// Interface principal do Chamado da API
export interface ChamadoAPI {
  id: number;
  tipo: ChamadoTipo;
  tipoAtendimento: ChamadoTipoAtendimento;
  descricao: string;
  natureza: ChamadoNatureza;
  status: ChamadoStatus;
  prioridade: ChamadoPrioridade;
  incluido: ChamadoData;
  alterado: ChamadoData;
  oculto: boolean;
  dataConclusao: string | null;
  dataInicioAtendimento?: ChamadoData;
  chamadoVinculado: ChamadoVinculado;
  empresa: ChamadoEmpresa;
  tempoEstimado: string;
  horasNegociadas: string;
  responsavel: ChamadoResponsavel;
  projeto: ChamadoProjeto;
  produto: ChamadoProduto;
  dataConclusaoPrevista: ChamadoDataConclusaoPrevista;
  chamadoExterno: any;
  departamentos: ChamadoDepartamento[];
  sla: ChamadoSLA;
  ultimaMovimentacao: ChamadoUltimaMovimentacao;
  iniciado: boolean;
  horasExecutadas: string;
  horasFaturadas: string;
  dataUltimaAlteracaoStatus: ChamadoData;
  ordensServico: string;
  solicitacao: string | null;
}

// Interface para o Chamado armazenado no banco de dados
export interface Chamado {
  id: string;
  chamado_id: number; // ID do chamado na API
  tipo_id: string;
  tipo_descricao: string;
  tipo_atendimento_id: string;
  tipo_atendimento_descricao: string;
  descricao: string;
  natureza_id: string;
  natureza_descricao: string;
  status_id: string;
  status_descricao: string;
  prioridade_id: string;
  prioridade_descricao: string;
  prioridade_cor: string;
  data_inclusao: string;
  data_alteracao: string;
  oculto: boolean;
  data_conclusao: string | null;
  data_inicio_atendimento: string | null;
  chamado_vinculado_id: number | null;
  chamado_vinculado_solicitacao: string | null;
  empresa_id: number;
  empresa_cnpj: string;
  empresa_nome: string;
  tempo_estimado: string;
  horas_negociadas: string;
  responsavel_id: number;
  responsavel_nome: string;
  projeto_id: number;
  projeto_nome: string;
  projeto_fechado: string | null;
  projeto_categoria_id: number;
  projeto_categoria_nome: string;
  produto_id: number;
  produto_nome: string;
  produto_icone: string;
  data_conclusao_prevista: string | null;
  /** Data de conclusão prevista na primeira importação; não muda quando a prevista atual é alterada na API. */
  primeira_data_conclusao_prevista?: string | null;
  chamado_externo: any;
  departamentos: string; // JSON string
  sla_executado: string;
  sla_prevista: string;
  sla_previstas: number;
  sla_executados: number;
  sla_porcentagem: number;
  ultima_movimentacao_data: string;
  ultima_movimentacao_diferenca: number;
  ultima_movimentacao_string: string;
  iniciado: boolean;
  horas_executadas: string;
  horas_faturadas: string;
  data_ultima_alteracao_status: string;
  ordens_servico: string;
  solicitacao: string | null;
  dados_api: any; // JSON com todos os dados originais da API
  ultima_sincronizacao: string;
  created_at: string;
  updated_at: string;
}

// Filtros para busca de chamados
export interface ChamadosFiltros {
  status?: string[];
  tipo?: string[];
  produto?: string[];
  empresa?: string[];
  responsavel?: string[];
  dataInicio?: string;
  dataFim?: string;
  /** Data de Conclusão (filtro na tela principal). Formato AAAA-MM-DD */
  dataConclusaoInicio?: string;
  dataConclusaoFim?: string;
  /** Data de Conclusão Prevista (usado na tela de Projeções). Formato ISO ou AAAA-MM-DD */
  dataConclusaoPrevistaInicio?: string;
  dataConclusaoPrevistaFim?: string;
  dataUltimaAlteracaoInicio?: string; // Formato AAAA-MM-DD para API
  dataUltimaAlteracaoFim?: string; // Formato AAAA-MM-DD para API
  search?: string;
}

// Status disponíveis
export const STATUS_CHAMADOS = [
  'finalizado',
  'cancelado',
  'aguardandoatendimento',
  'ematendimento',
  'aguardandoaprovacao',
  'aguardandoretornosolicitante',
  'aguardandoretornoterceiros',
  'aguardandogmud',
  'aguardandocomplementogmud',
  'aguardandorevisaogmud',
  'aguardandoefetuacaogmud',
  'revisaofinalgmud',
  'levantamentorequisitos'
] as const;

// Tipos disponíveis (correspondem ao portal / ordens_servico.tipo_chamado)
export const TIPOS_CHAMADOS = [
  'sustentacao',
  'item',
  'itemescopoprojeto',
  'itemsustentacaoprojeto'
] as const;

// Campos disponíveis para sincronização
export const CAMPOS_DISPONIVEIS = [
  'produto',
  'empresa',
  'responsavel',
  'departamentos',
  'sla',
  'horasExecutadas',
  'dataInicioAtendimento',
  'ultimaMovimentacao',
  'projeto',
  'ordensServico',
  'solicitacao'
] as const;

